/**
 * Client-side registry for AI draft sessions (#291).
 *
 * This is the smallest durable surface that makes in-flight drafts
 * discoverable after navigating away from `/admin/decks/new`: a
 * browser-local list keyed by the Agents SDK instance name. The chat
 * history itself still lives in the `DeckAuthorAgent` Durable Object;
 * this registry just preserves enough metadata for `/admin` to link
 * back to the right instance.
 */

import { useCallback, useEffect, useState } from "react";
import type { PreviewStatus } from "./deck-creation-snapshot";

export const DRAFT_SESSIONS_STORAGE_KEY = "slide-of-hand:draft-sessions:v1";

export type DraftSessionStatus = "new" | "generating" | "ready" | "error";

export interface DraftSession {
  agentName: string;
  status: DraftSessionStatus;
  createdAt: string;
  updatedAt: string;
  slug?: string;
  prompt?: string;
  phase?: string;
  draftId?: string;
  commitSha?: string;
  previewStatus?: PreviewStatus;
  previewUrl?: string;
  error?: string;
}

export type DraftSessionUpdate = Partial<
  Omit<DraftSession, "agentName" | "createdAt" | "updatedAt">
>;

function nowIso(): string {
  return new Date().toISOString();
}

export function isValidDraftAgentName(value: string | null): value is string {
  return typeof value === "string" && /^new-deck-[a-zA-Z0-9-]+$/.test(value);
}

function parseSessions(raw: string | null): DraftSession[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is DraftSession => {
      if (!item || typeof item !== "object") return false;
      const obj = item as Record<string, unknown>;
      return (
        isValidDraftAgentName(
          typeof obj.agentName === "string" ? obj.agentName : null,
        ) &&
        typeof obj.createdAt === "string" &&
        typeof obj.updatedAt === "string" &&
        (obj.status === "new" ||
          obj.status === "generating" ||
          obj.status === "ready" ||
          obj.status === "error")
      );
    });
  } catch {
    return [];
  }
}

export function readDraftSessions(): DraftSession[] {
  if (typeof window === "undefined") return [];
  const sessions = parseSessions(
    window.localStorage.getItem(DRAFT_SESSIONS_STORAGE_KEY),
  );
  return sessions.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

function writeDraftSessions(sessions: DraftSession[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    DRAFT_SESSIONS_STORAGE_KEY,
    JSON.stringify(sessions),
  );
  window.dispatchEvent(new Event("draft-sessions-change"));
}

export function upsertDraftSession(
  agentName: string,
  update: DraftSessionUpdate = {},
): DraftSession | null {
  if (!isValidDraftAgentName(agentName)) return null;
  const sessions = readDraftSessions();
  const existing = sessions.find((s) => s.agentName === agentName);
  const timestamp = nowIso();
  const next: DraftSession = {
    agentName,
    status: update.status ?? existing?.status ?? "new",
    createdAt: existing?.createdAt ?? timestamp,
    updatedAt: timestamp,
    ...(existing ?? {}),
    ...update,
  };
  const without = sessions.filter((s) => s.agentName !== agentName);
  writeDraftSessions([next, ...without].slice(0, 20));
  return next;
}

export function removeDraftSession(agentName: string) {
  const sessions = readDraftSessions().filter((s) => s.agentName !== agentName);
  writeDraftSessions(sessions);
}

export function draftSessionHref(session: Pick<DraftSession, "agentName">) {
  return `/admin/decks/new?session=${encodeURIComponent(session.agentName)}`;
}

export function useDraftSessions() {
  const [sessions, setSessions] = useState<DraftSession[]>(() =>
    readDraftSessions(),
  );

  const refresh = useCallback(() => setSessions(readDraftSessions()), []);

  useEffect(() => {
    window.addEventListener("storage", refresh);
    window.addEventListener("draft-sessions-change", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("draft-sessions-change", refresh);
    };
  }, [refresh]);

  return { sessions, refresh, remove: removeDraftSession };
}
